import { Alert, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"
import {
  type MilestoneTimelineIssue,
  type MilestoneTimelineWarning,
  type PlanningWindow,
  isBlockingTimelineIssue
} from "./lead-scope-milestone-timeline.ts"

const { Text } = Typography

interface LeadScopeMilestoneTimelineAlertProps {
  warnings: MilestoneTimelineWarning[]
  planning: PlanningWindow
  className?: string
}

/**
 * One summary for every milestone whose dates disagree with the lead's
 * planning window or with themselves — the timeline twin of the sales price
 * alert, and deliberately a single block rather than a warning per row. The
 * plan-window conflicts are advisory; a milestone ending before it starts is
 * refused by the server, so its presence turns the summary into an error.
 */
function LeadScopeMilestoneTimelineAlert({
  warnings,
  planning,
  className
}: LeadScopeMilestoneTimelineAlertProps) {
  const { t } = useTranslation()
  const { userFormat, normalizeDate } = useDateFormat()

  if (!warnings.length) return null

  const issues = warnings.flatMap((warning) => warning.issues)
  const blocking = issues.some(isBlockingTimelineIssue)
  const advisory = issues.some((issue) => !isBlockingTimelineIssue(issue))

  const showDate = (value?: string | null) =>
    normalizeDate(value, userFormat ?? undefined) ?? "—"

  const nameOf = (milestone: LeadFormDataPhase) =>
    milestone.name?.trim() || t("leadWorkspace.scope.milestones.unnamed")

  const describe = (
    milestone: LeadFormDataPhase,
    issue: MilestoneTimelineIssue
  ) =>
    t(`leadWorkspace.scope.milestones.timeline.${issue}`, {
      name: nameOf(milestone),
      start: showDate(milestone.start_date),
      end: showDate(milestone.end_date),
      planStart: showDate(planning.start_date),
      planEnd: showDate(planning.end_date)
    })

  return (
    <Alert
      showIcon
      type={blocking ? "error" : "warning"}
      className={className}
      data-testid={"milestones-timeline-alert"}
      message={t("leadWorkspace.scope.milestones.timeline.title", {
        count: warnings.length
      })}
      description={
        <>
          <ul className={"m-0 list-disc pl-5"}>
            {warnings.map(({ milestone, issues }) => (
              <li key={milestone.id}>
                {issues.map((issue, index) => (
                  <React.Fragment key={issue}>
                    {index > 0 && " "}
                    {describe(milestone, issue)}
                  </React.Fragment>
                ))}
              </li>
            ))}
          </ul>
          {blocking && (
            <Text type={"danger"} className={"mt-1 block text-xs"}>
              {t("leadWorkspace.scope.milestones.timeline.blocking")}
            </Text>
          )}
          {advisory && (
            <Text type={"secondary"} className={"mt-1 block text-xs"}>
              {t("leadWorkspace.scope.milestones.timeline.advisory")}
            </Text>
          )}
        </>
      }
    />
  )
}

export { LeadScopeMilestoneTimelineAlert }
